import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { RingLoader } from "react-spinners";
import { FaDownload, FaEye, FaSearch, FaCalendarAlt, FaTimes, FaInfoCircle } from "react-icons/fa";
import { BiLeftArrow, BiRightArrow } from "react-icons/bi";
import axios from "axios";
import DatePicker, { registerLocale } from "react-datepicker";
import { pt } from "date-fns/locale/pt";
import { getAuthToken } from "../../services/Auth";
import { ATTACHMENTS_BASE } from "../../constants/endpoints";
import MessageToUse from "../messagesToUse/MessagesToUse";
import { articleInformation } from "../messagesToUse/list";
import "react-datepicker/dist/react-datepicker.css";
import "./attachmentsSearch.scss";

registerLocale("pt", pt);

const ITEMS_PER_PAGE = 20;

function AttachmentsSearch() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDocs, setSelectedDocs] = useState([]);
    const [downloadingBatch, setDownloadingBatch] = useState(false);
    const [seeBalloon, setSeeBalloon] = useState(false);
    const balloonRef = useRef(null);
    
    const selectedCompany = useSelector((state) => state.selectedCompany.data);
    const selectedLanguage = useSelector((state) => state.selectedLanguage.data);

    // Close balloon on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (balloonRef.current && !balloonRef.current.contains(event.target)) {
                setSeeBalloon(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const formatDate = (dateObj) => {
        if (!dateObj) return "";
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const fetchAttachments = async (clientId, start = "", end = "") => {
        if (!clientId) return;
        setLoading(true);
        setAttachments([]);
        setSelectedDocs([]);
        try {
            const token = getAuthToken();
            const params = {};
            if (start) params.date_start = start;
            if (end) params.date_end = end;

            const response = await axios.get(`${ATTACHMENTS_BASE}${clientId}`, {
                headers: {
                    Authorization: `token ${token}`,
                    "Accept-Language": selectedLanguage?.lang || "pt"
                },
                params: params
            });
            setAttachments(response.data);
        } catch (error) {
            const errorDetail = error.response?.data?.detail;
            const message = Array.isArray(errorDetail) ? errorDetail[0] : t("Erro ao carregar os documentos.");
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch whenever selectedCompany or date range change (only triggers when both dates are picked or when cleared)
    useEffect(() => {
        if (selectedCompany?.id) {
            if (startDate && !endDate) {
                return; // Aguarda até que a data de fim seja selecionada
            }
            const startStr = formatDate(startDate);
            const endStr = formatDate(endDate);
            fetchAttachments(selectedCompany.id, startStr, endStr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany?.id, startDate, endDate, selectedLanguage?.lang]);

    // Reset pagination and selection when search term, date range, or company changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, startDate, endDate, selectedCompany?.id]);

    const handleDownload = async (docId, filename, extension) => {
        if (!selectedCompany?.id) return;
        try {
            const token = getAuthToken();
            const downloadUrl = `${ATTACHMENTS_BASE}${selectedCompany.id}/download/${docId}`;
            
            const response = await axios.get(downloadUrl, {
                headers: {
                    Authorization: `token ${token}`
                },
                responseType: "blob"
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            
            const fullFilename = extension ? `${filename}.${extension}` : filename;
            link.setAttribute("download", fullFilename);
            document.body.appendChild(link);
            link.click();
            
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(t("Erro ao descarregar o ficheiro."));
        }
    };

    const handleOpenFile = async (docId, filename, extension) => {
        if (!selectedCompany?.id) return;
        try {
            const token = getAuthToken();
            const downloadUrl = `${ATTACHMENTS_BASE}${selectedCompany.id}/download/${docId}`;
            
            const response = await axios.get(downloadUrl, {
                headers: {
                    Authorization: `token ${token}`,
                    "Accept-Language": selectedLanguage?.lang || "pt"
                },
                responseType: "blob"
            });

            let mimeType = "application/octet-stream";
            const ext = (extension || "").toLowerCase();
            if (ext === "pdf") mimeType = "application/pdf";
            else if (ext === "png") mimeType = "image/png";
            else if (ext === "jpg" || ext === "jpeg") mimeType = "image/jpeg";
            else if (ext === "txt") mimeType = "text/plain";

            const fileBlob = new Blob([response.data], { type: mimeType });
            const url = window.URL.createObjectURL(fileBlob);
            window.open(url, "_blank");
        } catch (error) {
            toast.error(t("Erro ao abrir o ficheiro."));
        }
    };

    const handleToggleDoc = (docId) => {
        setSelectedDocs((prev) => {
            if (prev.includes(docId)) {
                return prev.filter((id) => id !== docId);
            }
            if (prev.length >= 20) {
                toast.warning(t("Máximo de 20 documentos permitidos para download simultâneo."));
                return prev;
            }
            return [...prev, docId];
        });
    };

    const handleToggleSelectAll = () => {
        const pageDocIds = paginatedAttachments.map((att) => att.doc_id);
        const allPageSelected = pageDocIds.length > 0 && pageDocIds.every((id) => selectedDocs.includes(id));

        if (allPageSelected) {
            setSelectedDocs((prev) => prev.filter((id) => !pageDocIds.includes(id)));
        } else {
            setSelectedDocs((prev) => {
                const newSelection = [...prev];
                let reachedLimit = false;
                for (const id of pageDocIds) {
                    if (!newSelection.includes(id)) {
                        if (newSelection.length < 20) {
                            newSelection.push(id);
                        } else {
                            reachedLimit = true;
                        }
                    }
                }
                if (reachedLimit) {
                    toast.info(t("Máximo de 20 documentos selecionados."));
                }
                return newSelection;
            });
        }
    };

    const handleDownloadBatch = async () => {
        if (!selectedCompany?.id || selectedDocs.length === 0) return;
        setDownloadingBatch(true);
        try {
            const token = getAuthToken();
            const batchUrl = `${ATTACHMENTS_BASE}${selectedCompany.id}/download_batch`;

            const response = await axios.post(
                batchUrl,
                { doc_ids: selectedDocs },
                {
                    headers: {
                        Authorization: `token ${token}`,
                        "Accept-Language": selectedLanguage?.lang || "pt",
                    },
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `documentos_${selectedCompany.id}.zip`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t("Download concluído com sucesso."));
            setSelectedDocs([]);
        } catch (error) {
            toast.error(t("Erro ao descarregar os documentos selecionados."));
        } finally {
            setDownloadingBatch(false);
        }
    };

    // Local filtering
    const filteredAttachments = attachments.filter((att) => {
        const query = searchTerm.toLowerCase();
        return (
            (att.filename && att.filename.toLowerCase().includes(query)) ||
            (att.invoice_number && att.invoice_number.toLowerCase().includes(query)) ||
            (att.internal_doc && att.internal_doc.toLowerCase().includes(query)) ||
            (att.date && att.date.includes(query)) ||
            (att.document_type && att.document_type.toLowerCase().includes(query))
        );
    });

    const totalPages = Math.ceil(filteredAttachments.length / ITEMS_PER_PAGE) || 1;

    const goToPage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const paginatedAttachments = filteredAttachments.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    return (
        <div className="attachments-search-container">
            {/* Botão de Informação no Topo Direito (Consistente com restantes páginas) */}
            <div className="d-flex justify-content-end mb-2" style={{ position: 'relative' }}>
                <button
                    className="download-btn-interactive"
                    style={{ background: 'none', border: 'none', padding: '4px' }}
                    onClick={() => setSeeBalloon(!seeBalloon)}
                    onMouseOver={() => setSeeBalloon(true)}
                    title={t("Informações da Tabela")}
                >
                    <FaInfoCircle color={'blue'} size={18} />
                </button>
                {seeBalloon && (
                    <div ref={balloonRef} className="informationBalloon">
                        <small>
                            {articleInformation.map((item) =>
                                item.state === "documentos" ? (
                                    <MessageToUse
                                        key={item.label}
                                        title={t(item.label)}
                                        text={t(item.description)}
                                    />
                                ) : null
                            )}
                        </small>
                    </div>
                )}
            </div>

            {/* Local & Remote Filter Bar */}
            <div className="filter-bar mb-4">
                {/* Campo de pesquisa de texto */}
                <div className="search-wrapper">
                    <div className="custom-input-group" title={t("Filtrar localmente por n.º documento, fatura, nome...")}>
                        <span className="search-icon">
                            <FaSearch />
                        </span>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={t("Filtrar localmente por n.º documento, fatura, nome...")}
                            title={t("Filtrar localmente por n.º documento, fatura, nome...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading}
                        />
                        {searchTerm && (
                            <button 
                                className="btn-clear-search" 
                                type="button" 
                                onClick={() => setSearchTerm("")}
                                title={t("Limpar pesquisa")}
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filtro de Calendário de Intervalo Único */}
                <div className="datepicker-wrapper">
                    <div className="custom-input-group" title={t("Intervalo de datas")}>
                        <span className="calendar-icon">
                            <FaCalendarAlt />
                        </span>
                        <DatePicker
                            locale={selectedLanguage?.lang === "pt" ? "pt" : undefined}
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(update) => setDateRange(update)}
                            isClearable={true}
                            placeholderText={t("Intervalo de datas")}
                            title={t("Intervalo de datas")}
                            className="date-range-picker-input"
                            dateFormat="yyyy-MM-dd"
                            disabled={loading}
                        />
                    </div>
                </div>

                {/* Ações em Lote à Direita */}
                {selectedDocs.length > 0 && (
                    <div className="batch-actions-wrapper d-flex align-items-center ml-auto">
                        <button
                            className="btn-batch-download d-flex align-items-center"
                            onClick={handleDownloadBatch}
                            disabled={downloadingBatch}
                            title={t("Descarregar documentos selecionados em ficheiro ZIP")}
                        >
                            {downloadingBatch ? (
                                <span>{t("A processar...")}</span>
                            ) : (
                                <>
                                    <FaDownload size={12} className="mr-2" />
                                    <span>
                                        {t("Descarregar Selecionados")} ({selectedDocs.length}/20)
                                    </span>
                                </>
                            )}
                        </button>
                        <button
                            className="btn-clear-selection ml-2"
                            onClick={() => setSelectedDocs([])}
                            title={t("Desmarcar todos")}
                        >
                            {t("Desmarcar")}
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="d-flex justify-content-center my-5">
                    <RingLoader color="#002C4B" size={60} />
                </div>
            ) : (
                <div className="results-container">
                    {attachments.length === 0 ? (
                        <p className="text-center text-muted my-5">
                            {t("Não foram encontrados documentos para a empresa selecionada.")}
                        </p>
                    ) : filteredAttachments.length === 0 ? (
                        <p className="text-center text-muted my-5">
                            {t("Nenhum documento corresponde ao filtro introduzido.")}
                        </p>
                    ) : (
                        <>
                            <table className="attachments-table">
                                <thead>
                                    <tr className="colorHead">
                                        <th className="col-checkbox">
                                            <input
                                                type="checkbox"
                                                className="doc-checkbox"
                                                checked={
                                                    paginatedAttachments.length > 0 &&
                                                    paginatedAttachments.every((att) => selectedDocs.includes(att.doc_id))
                                                }
                                                onChange={handleToggleSelectAll}
                                                title={t("Selecionar todos")}
                                            />
                                        </th>
                                        <th>{t("Data")}</th>
                                        <th>{t("Tipo")}</th>
                                        <th>{t("N.º Documento")}</th>
                                        <th>{t("Fatura / Guia")}</th>
                                        <th style={{ width: "1%", textAlign: "center" }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedAttachments.map((att) => (
                                        <tr key={att.doc_id} className="tableTr">
                                            <td className="col-checkbox">
                                                <input
                                                    type="checkbox"
                                                    className="doc-checkbox"
                                                    checked={selectedDocs.includes(att.doc_id)}
                                                    onChange={() => handleToggleDoc(att.doc_id)}
                                                />
                                            </td>
                                            <td>{att.date}</td>
                                            <td>{att.document_type}</td>
                                            <td>{att.internal_doc || "-"}</td>
                                            <td>{att.invoice_number || "-"}</td>
                                            <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', width: '1%', textAlign: 'center' }}>
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <button
                                                        title={t("Abrir documento")}
                                                        className="download-btn-interactive"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', marginRight: '6px' }}
                                                        onClick={() => handleOpenFile(att.doc_id, att.filename, att.extension)}
                                                    >
                                                        <FaEye size={15} color="#002C4B" />
                                                    </button>
                                                    <button
                                                        title={t("Download")}
                                                        className="download-btn-interactive"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                                                        onClick={() => handleDownload(att.doc_id, att.filename, att.extension)}
                                                    >
                                                        <FaDownload size={14} color="#002C4B" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Standard Application Pagination */}
                            {filteredAttachments.length > 0 && (
                                <div className="d-flex justify-content-flex-end align-items-center mt-3 mb-2">
                                    <span
                                        style={{
                                            borderRight: "1px solid lightgray",
                                            paddingRight: "32px",
                                            marginRight: "32px",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t("Página")} {currentPage} {t("de")} {totalPages}
                                    </span>
                                    <button 
                                        className="btnTable" 
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                        title={t("Página Anterior")}
                                    >
                                        <BiLeftArrow size={18} />
                                    </button>
                                    <button
                                        className="btnTable"
                                        style={{
                                            marginRight: "32px",
                                        }}
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage >= totalPages}
                                        title={t("Página Seguinte")}
                                    >
                                        <BiRightArrow size={18} />
                                    </button>
                                    <input
                                        style={{ width: "64px", fontSize: "12px", textAlign: "center" }}
                                        type="number"
                                        min="1"
                                        max={totalPages}
                                        value={currentPage}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value, 10);
                                            if (!isNaN(val)) goToPage(val);
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default AttachmentsSearch;
